import {
    Dialog,
    DialogContent,
    DialogFooter,
    // DialogTitle,
} from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { Bolt, Folder, Users, X } from 'lucide-react';

import { ConfigureTab } from './assistant-tabs/configure-tab';
import { KnowledgeBaseTab } from './assistant-tabs/knowledge-base-tab';
import { SharingTab } from './assistant-tabs/sharing-tab';
// import { PermissionTab } from './assistant-tabs/permission-tab';
import { useTheme } from 'components/core/theme-provider';
import { useCustomGptStore } from 'state/store/chat/use-custom-gpt-store';
import { useCreateCustomGpt, useDeleteCustomGpt, useUploadCustomGptFile, useUpdateCustomGpt } from '../hooks/use-custom-gpt';
import { useToast } from 'hooks/use-toast';

interface CreateAssistantModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const CreateAssistantModal = ({ isOpen, setIsOpen }: CreateAssistantModalProps) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const {
        activeStep,
        setActiveStep,
        reset,
        customGptId,
        name,
        systemPrompt,
        modelName,
        temperature,
        logoUrl: logoId
    } = useCustomGptStore();
    const { mutate: createGpt, isPending: isCreating } = useCreateCustomGpt();
    const { mutate: updateGpt, isPending: isUpdatingGpt } = useUpdateCustomGpt();
    const { mutate: uploadFile, isPending: isUploading } = useUploadCustomGptFile();
    const { mutate: deleteGpt } = useDeleteCustomGpt();
    const { toast } = useToast();
    const { localFiles, isEditMode } = useCustomGptStore();

    const tabs = [
        { id: 'configure', label: t('CONFIGURE') || 'Configure', icon: Bolt, component: ConfigureTab },
        { id: 'knowledge-base', label: t('KNOWLEDGE_BASE') || 'Knowledge Base', icon: Folder, component: KnowledgeBaseTab },
        { id: 'sharing', label: t('SHARING') || 'Sharing', icon: Users, component: SharingTab },
        // { id: 'permission', label: t('PERMISSION') || 'Permission', icon: Lock, component: PermissionTab },
    ];

    const handleNext = () => {
        const currentIndex = tabs.findIndex(tab => tab.id === activeStep);
        if (currentIndex < tabs.length - 1) {
            setActiveStep(tabs[currentIndex + 1].id);
        }
    };

    const handleCancel = () => {
        if (customGptId && !isEditMode) {
            deleteGpt(customGptId);
        }
        reset();
        setIsOpen(false);
    };

    const handleSubmit = async () => {
        if (!name.trim() || !systemPrompt.trim() || !modelName) {
            toast({
                title: t('VALIDATION_ERROR') || 'Validation Error',
                description: t('FILL_ALL_REQUIRED_FIELDS') || 'Please fill in all required fields (Name, System Instructions, and Model).',
                variant: 'destructive',
            });
            return;
        }

        const payload = {
            name,
            description: '',
            system_prompt: systemPrompt,
            model_name: modelName,
            temperature,
            logo_url: logoId,
        };

        const onMutationSuccess = async (data: any) => {
            const gptId = data.id;

            if (localFiles.length > 0) {
                toast({
                    title: t('UPLOADING_FILES') || 'Uploading files...',
                    description: t('PLEASE_WAIT') || 'Please wait while we upload your files.',
                });

                for (const file of localFiles) {
                    try {
                        await new Promise((resolve, reject) => {
                            uploadFile({ customGptId: gptId, file }, {
                                onSuccess: resolve,
                                onError: reject
                            });
                        });
                    } catch (error) {
                        console.error(`Failed to upload file ${file.name}:`, error);
                        toast({
                            title: t('UPLOAD_FAILED') || 'Upload Failed',
                            description: t('FAILED_TO_UPLOAD_FILE', { name: file.name }),
                            variant: 'destructive',
                        });
                    }
                }
            }

            toast({
                title: t('SUCCESS') || 'Success',
                description: isEditMode
                    ? (t('ASSISTANT_UPDATED_SUCCESSFULLY') || 'Assistant updated successfully!')
                    : (t('ASSISTANT_CREATED_SUCCESSFULLY') || 'Assistant created successfully!'),
            });
            reset();
            setIsOpen(false);
        };

        const onMutationError = (error: any) => {
            console.error('Failed to process assistant:', error);
            toast({
                title: t('ERROR') || 'Error',
                description: isEditMode
                    ? (t('FAILED_TO_UPDATE_ASSISTANT') || 'Failed to update assistant. Please try again.')
                    : (t('FAILED_TO_CREATE_ASSISTANT') || 'Failed to create assistant. Please try again.'),
                variant: 'destructive',
            });
        };

        if (isEditMode && customGptId) {
            updateGpt({ customGptId, payload }, {
                onSuccess: onMutationSuccess,
                onError: onMutationError
            });
        } else {
            createGpt(payload, {
                onSuccess: onMutationSuccess,
                onError: onMutationError
            });
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className={`flex flex-col gap-4 p-0 max-w-[649px] overflow-hidden rounded-lg`} hideClose>
                <div className={`flex flex-col p-6 rounded-lg ${theme === 'dark' ? "bg-black-400" : "bg-neutral-50"}`}>
                    <div className="flex flex-col gap-[5px]">
                        <div className="flex justify-between items-start">
                            <h2 className={`${theme === 'dark' ? "text-typography-white" : "text-black-500"} text-2xl font-inter font-semibold leading-9 break-words`}>
                                {isEditMode ? (t('EDIT_ASSISTANT') || 'Edit Assistant') : (t('CREATE_ASSISTANT') || 'Create Assistant')}
                            </h2>

                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <p className={`${theme === 'dark' ? "text-typography-white-stroke" : "text-typography-grey-1-secondary"} text-sm font-normal leading-[22px] break-words`}>
                            {t('CREATE_ASSISTANT_DESCRIPTION') ||
                                'Configure your assistant with custom instructions, knowledge base, and sharing settings'}
                        </p>
                    </div>


                    <Tabs value={activeStep} onValueChange={setActiveStep} className="flex-1 flex flex-col overflow-hidden">
                        <TabsList
                            className={`w-full p-1 inline-flex items-center justify-between rounded-[6px] border border-[#816C51] ${theme === 'dark' ? "bg-black-400" : "bg-[#FFF0D080]"}`}
                        >
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <TabsTrigger
                                        key={tab.id}
                                        value={tab.id}
                                        className={`flex flex-1 items-center justify-center gap-[6px] px-3 py-[6px] rounded-[4px] text-sm font-nunito font-semibold leading-[22px] tracking-[0.14px] transition-all data-[state=active]:bg-[#816C51] data-[state=active]:text-[#FFFFFF] ${theme === 'dark' ? "text-typography-white-stroke" : "text-typography-grey-1-secondary"}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span className="hidden sm:inline">{tab.label}</span>
                                    </TabsTrigger>
                                );
                            })}
                        </TabsList>

                        <div className="flex-1 mt-4">
                            {tabs.map((tab) => (
                                <TabsContent key={tab.id} value={tab.id} className="mt-0 h-full">
                                    <tab.component />
                                </TabsContent>
                            ))}
                        </div>
                    </Tabs>
                </div>
                <DialogFooter>
                    <button
                        onClick={handleCancel}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${theme === 'dark' ? "text-typography-white-stroke" : "text-typography-grey-2-secondary"}`}
                    >
                        {t('CANCEL') || 'Cancel'}
                    </button>
                    {activeStep !== 'sharing' ? (
                        <button
                            onClick={handleNext}
                            className={`bg-primary-600 hover:bg-[#a68d6c] text-typography-white px-6 py-2 rounded-lg text-sm font-medium transition-colors`}
                        >
                            {t('NEXT') || 'Next'}
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isCreating || isUpdatingGpt || isUploading}
                            className={`bg-primary-600 hover:bg-[#a68d6c] text-typography-white px-6 py-2 rounded-lg text-sm font-medium transition-colors`}
                        >
                            {isCreating || isUpdatingGpt || isUploading
                                ? (t('SAVING') || 'Saving...')
                                : isEditMode
                                    ? (t('SAVE_CHANGES') || 'Save Changes')
                                    : (t('CREATE_ASSISTANT') || 'Create Assistant')}
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
